<template>
	<modal :showing="showing" size="large" @closed="$emit('cancelled')">
		<h5 class="t-text-md t-font-semibold t-text-gray-900" slot="title">
			{{ modalTitle }}
		</h5>
		
		<form @submit.prevent="submitForm()" >
			<div class="t-bg-white t-rounded t-px-8 t-pt-6 t-pb-8" slot="body">
				<div class="t-mb-4">
					<label for="commonname-input" class="t-block t-text-gray-500 t-font-bold t-mb-1 md:t-mb-0 t-pr-4">Common Name<span class="t-text-red-500 t-text-md">*</span></label>
					<input :class="{ 'is-invalid': errors.has('commonName') }"
						name="commonName" v-validate="'required'"
						id="commonname-input"
						type="text"
						class="t-bg-gray-200 t-appearance-none t-border-2 t-border-gray-200 t-rounded t-w-full t-py-2 t-px-4 t-text-gray-700 t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-purple-500"
						v-model="form.commonName" placeholder="e.g: Methylated spirits">
					<div v-show="errors.has('commonName')" class="t-text-red-500 t-text-sm">
						Please enter a common name
					</div>
					<small class="t-text-gray-500 t-text-sm">
						Your name for this product - normally matching your label - which gets put onto the DG form
					</small>
				</div>

				<div class="md:t-w-full t-flex">
					<div class="t-w-3/4 t-mr-4 t-mb-4">
						<label class="t-block t-text-gray-500 t-font-bold t-mb-1 md:t-mb-0 t-pr-4">UN Number<span class="t-text-red-500 t-text-md">*</span></label>
						<autocomplete
							style="width: 100%"
							v-if="autocompleteReady"
							source="/api/un-numbers?q="
							results-property="value"
							results-display="text"
							placeholder="Enter UN number or chemical name"
							:initialValue="form.unId"
							:initialDisplay="form.unName"
							:invalid="unError"
							@selected="unSelected"
							name="unId">
						</autocomplete>

						<div v-if="unError" style="display:block" class="t-text-red-500 t-text-sm">
							Please select a UN number
						</div>
					</div>
					
					<div class="t-w-1/4 t-mx-4 t-mb-4 required">
						<label class="t-block t-text-gray-500 t-font-bold t-mb-1 md:t-mb-0 t-pr-4" for="flashPoint-input">Flash Point<span class="t-text-red-500 t-text-md">*</span></label>
						<input name="flashPoint"
						id="flashPoint-input"
						:class="{ 'is-invalid': errors.has('flashPoint') }"
						v-validate="'required'"
						placeholder="23 °C"
						type="text" 
						class="t-bg-gray-200 t-appearance-none t-border-2 t-border-gray-200 t-rounded t-w-full t-py-2 t-px-4 t-text-gray-700 t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-purple-500" 
						v-model="form.flashPoint"
						@selected="unSelected"
						>
						<div v-show="errors.has('flashPoint')" class="t-text-red-500 t-text-sm">
							Please enter a flash point
						</div>
					</div>
					<div class="t-w-1/4 t-ml-4 t-mb-4">
						<label class="t-block t-text-gray-500 t-font-bold t-mb-1 md:t-mb-0 t-pr-4">Marine Pollutant</label>
						<select v-model="form.marinePollutant" class="t-bg-gray-200 t-appearance-none t-border-2 t-border-gray-200 t-rounded t-w-full t-py-2 t-px-4 t-text-gray-700 t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-purple-500">
							<option value="0">No</option>
							<option value="1">Yes</option>
						</select>
					</div>
				</div>

				<div class="t-mb-4 required">
					<label for="properShippingName-input" class="t-block t-text-gray-500 t-font-bold t-mb-1 md:t-mb-0 t-pr-4">Proper Shipping Name<span class="t-text-red-500 t-text-md">*</span></label>
					<input v-bind:class="{ 'is-invalid': errors.has('properShippingName') }"
						name="properShippingName" v-validate="'required'"
						id="properShippingName-input"
						type="text"
						class="t-bg-gray-200 t-appearance-none t-border-2 t-border-gray-200 t-rounded t-w-full t-py-2 t-px-4 t-text-gray-700 t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-purple-500"
						:disabled="properShippingDisabled"
						v-model="form.properShippingName" placeholder="e.g: Ammonium Picrate">
					<div v-show="errors.has('properShippingName')" class="t-text-red-500 t-text-sm">
						Please enter a proper shipping name
					</div>
					<small class="t-text-gray-500 t-text-sm">
						The proper shipping name for this product, please edit the auto generated shipping name if it is not correct
					</small>
				</div>

				<div class="t-w-full t-flex">
					<div class="t-w-2/4 t-mr-2 t-mb-4">
						<label class="t-block t-text-gray-500 t-font-bold t-mb-1 md:t-mb-0 t-pr-4">Sizes</label>
						<input 
							type="text" 
							class="t-bg-gray-200 t-appearance-none t-border-2 t-border-gray-200 t-rounded t-w-full t-py-2 t-px-4 t-text-gray-700 t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-purple-500" 
							placeholder="Product code (optional)" 
							v-model="sizesForm.code"
						>
					</div>

					<div class="t-w-1/4 t-mr-2 t-mb-4 md:t-mt-6">
						<input 
							name="amount" 
							type="number" 
							step="0.001" 
							class="t-bg-gray-200 t-appearance-none t-border-2 t-border-gray-200 t-rounded t-w-full t-py-2 t-px-4 t-text-gray-700 t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-purple-500" 
							placeholder="Amount" 
							v-model="sizesForm.amount"
							:invalid="sizesError"
							@selected="unSelected"
						>
						<div v-if="sizesError" style="display:block" class="t-text-red-500 t-text-sm">
							Please select an amount
						</div>
					</div>
					
					<div class="t-w-1/4 t-mr-2 t-mb-4 md:t-mt-6">
						<select class="t-bg-gray-200 t-appearance-none t-border-2 t-border-gray-200 t-rounded t-w-full t-py-2 t-px-4 t-text-gray-700 t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-purple-500" v-model="sizesForm.unit">
							<option value="g">g</option>
							<option value="kg">kg</option>
							<option value="ml">ml</option>
							<option value="L">litre</option>
						</select>
					</div>
					<div class="t-w-1/4 t-mr-2 t-mb-4 md:t-mt-6" v-if="!hasProduct()">
						<button 
							v-if="!hasProduct()" 
							:disabled="sizeSaving || !this.sizesForm.amount" 
							type="submit" 
							class="t-bg-blue-500 hover:t-bg-blue-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline" 
							@click.prevent="tempsize()"
						>
							<i v-if="sizeSaving" class="fa fa-spinner fa-spin"></i>
							<span v-else>Add</span>
						</button>
					</div>

					<div class="t-w-1/4 t-mr-2 t-mb-4 md:t-mt-6" v-if="hasProduct()">
						<button 
							v-if="hasProduct()" 
							:disabled="sizeSaving || !this.sizesForm.amount" 
							type="submit" 
							class="t-bg-blue-500 hover:t-bg-blue-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline" 
							@click.prevent="tempAddsize()"
						>
							<i v-if="sizeSaving" class="fa fa-spinner fa-spin"></i>
							<span v-else>Add</span>
						</button>
					</div>

					<div class="t-w-3/4 t-mr-2 t-mb-4">
						<label for="msdFile-upload" class="t-block t-text-gray-500 t-font-bold t-mb-1 md:t-mb-0 md:t-pl-3">MDS Sheet Upload<span class="t-text-red-500 t-text-md">*</span></label>
						<el-upload
							v-model="msdFiles"
							name="msdFile"
							id="msdFile-upload"
							drag
							accept=".pdf"
							:on-remove="handleRemove"
							:action="'/send-freight/dangerous-goods/1/uploadMSD/'"
							:before-upload="duringDeliveryUpload"
							:file-list="fileList"
							multiple>
								<i class="el-icon-upload"></i>
								<div class="el-upload__text">Drop the PDF file here or <em>click to upload</em></div>
								<!-- <div v-show="errors.has('msdFile')" class="el-upload__tip" slot="tip" style=" display: none; width: 100%; margin-top: 0.25rem; font-size: 80%; color: #dc3545;">
									Please upload the MDS document for this DG.
								</div> -->
						</el-upload>
					</div>
				</div>

				<label v-if="tempSizes.length">Saved Sizes</label>
				<div class="t-flex" v-if="tempSizes.length">
					<table class="t-table-fixed t-w-1/2 t-text-md t-bg-white t-rounded t-mb-4">
						<tr v-for="(size, index) in tempSizes" :key="size.id">
							<td>
							<table class="t-w-full t-text-md bg-white t-rounded t-mb-4">
								<tr class="t-border-b">
									<td class="t-w-3/4 t-px-4 t-py-2">{{ size.code }}</td>
									<td class="t-w-1/4 t-px-4 t-py-2">{{ parseFloat(size.amount) }}</td>
									<td class="t-w-1/4 t-px-4 t-py-2">{{ size.unit }}</td>
									<td class="t-w-1/4 t-px-4 t-py-2">
										<button  @click="deleteTempSize(index)" type="button" class="t-bg-red-500 hover:t-bg-red-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline">
												<i class="fa fa-trash"></i>
										</button>
									</td>
								</tr>
							</table>	
							</td>
						</tr>
					</table>
				</div>
				
				<label v-if="sizes.length">Saved Sizes</label>
				<div v-if="sizes.length" class="t-flex">
					<table class="t-table-fixed t-w-1/2 t-text-md t-bg-white t-rounded t-mb-4">
						<tr v-for="(size, index) in sizes" :key="size.id">
							<td v-if="sizeEditing && sizeEditing.id == size.id">
									<tr class="t-border-b">
										<td class="t-w-1/2 t-py-2 t-pr-2">
											<input 
												type="text" 
												class="t-bg-gray-200 t-appearance-none t-border-2 t-border-gray-200 t-rounded t-w-full t-py-2 t-px-4 t-text-gray-700 t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-purple-500" 
												placeholder="Product code (optional)"
												v-model="editingForm.code">
										</td>
										<td class="t-w-1/4 t-py-2 t-pr-2">
											<input 
												name="amount" 
												type="number" 
												step="0.001" 
												class="t-bg-gray-200 t-appearance-none t-border-2 t-border-gray-200 t-rounded t-w-full t-py-2 t-px-4 t-text-gray-700 t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-purple-500" 
												placeholder=""
												v-model="editingForm.amount"  
												v-validate="'required'"
											>
											<div style="display:block" class="t-text-red-500 t-text-sm">
												{{ errors.first('amount') }}
											</div>
										</td>
										<td class="t-w-1/4 t-py-2 t-pr-2">
											<select 
												class="t-bg-gray-200 t-appearance-none t-border-2 t-border-gray-200 t-rounded t-w-full t-py-2 t-px-4 t-text-gray-700 t-leading-tight focus:t-outline-none focus:t-bg-white focus:t-border-purple-500" 
												v-model="editingForm.unit"
											>
												<option value="g">g</option>
												<option value="kg">kg</option>
												<option value="ml">ml</option>
												<option value="L">litre</option>
											</select>
										</td>
										<td class="t-w-1/4 t-py-2 t-pr-2">
											<button :disabled="sizeUpdating === size" @click.prevent="updateSize(size, index)" type="button" class="t-bg-green-500 hover:t-bg-green-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline">
												<i v-if="sizeUpdating === size" class="fa fa-spinner fa-spin"></i>
												<i v-else class="fa fa-check"></i>
											</button>
										</td>
									</tr>
							</td>
							<td v-else>
								<tr class="t-border-b">
									<td class="t-w-3/4 t-px-4 t-py-2">{{ size.code }}</td>
									<td class="t-w-1/4 t-px-4 t-py-2">{{ parseFloat(size.amount) }}</td>
									<td class="t-w-1/4 t-px-4 t-py-2">{{ size.unit }}</td>
									<td class="t-flex t-w-1/2 t-px-4 t-py-2">
										<button @click="editSize(size)" type="button" class="t-bg-blue-500 hover:t-bg-blue-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline">
											<i v-if="sizeEditing === size" class="fa fa-spinner fa-spin"></i>
											<i v-else class="fa fa-pencil"></i>
										</button>
										<button :disabled="sizeDeleting === index" @click="deleteSize(index)" type="button" class="t-bg-red-500 hover:t-bg-red-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline">
											<i v-if="sizeDeleting === index" class="fa fa-spinner fa-spin"></i>
											<i v-else class="fa fa-trash"></i>
										</button>
									</td>
								</tr>
								
							</td>
						</tr>
					</table>
				</div>
			</div>
			<div class="t-flex t-items-center t-justify-end t-px-8 t-pb-8">
				<button 
					@click="$emit('cancelled')" 
					type="button" 
					class="t-bg-gray-500 hover:t-bg-gray-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline" 
					data-dismiss="modal"
				>Close</button>
				<button 
					:disabled="saving" 
					type="submit" 
					class="t-bg-green-500 hover:t-bg-green-700 t-text-white t-font-bold t-py-2 t-px-4 t-mx-2 t-rounded focus:t-outline-none focus:t-shadow-outline"
				>
					<i v-if="saving" class="fa fa-spinner fa-spin"></i>
					<span v-else>{{ submitButtonText }}</span>
				</button>
			</div>
		</form>
    </modal>
</template>

<script>
	import Autocomplete from '../../utils/Autocomplete';
	import Modal from '../../utils/Modal';

    export default {
        name: 'dangerous-goods-form',
        props: ['product', 'showing'],
		components: { Autocomplete, Modal },
		$_veeValidate: {
			validator: 'new'
		},

        data() {
            return {
				form: {
					commonName: '',
					unId: '',
					unName: '',
					flashPoint: 'NIL',
					marinePollutant: '0',
					properShippingName: '',
				},
				sizesForm: {
					code: '',
					amount: '',
					unit: 'kg',
				},
				editingForm: {
					code: '',
					amount: '',
					unit: ''
				},
				sizeSaving: false,
				sizeDeleting: false,
				sizeUpdating: false,
				sizeEditing: null,
				sizeAdd: false,
				sizes: [],
				autocompleteReady: false,
				saving: false,
				unError: false,
				isHidden: false,
				tempSizes: [],
				idtest:'',
				sizesError: false,
				properShippingDisabled: true,
				msdFiles: [],
				fileList: [],
            }
		},

		mounted() {
			if (this.product) {
				this.form = {
					commonName: this.product.name,
					unId: this.product.un.data.id,
					unName: this.product.unDetails,
					flashPoint: this.product.flashPoint,
					marinePollutant: this.product.marinePollutant,
					properShippingName: this.product.properShippingName,
				}
				if(this.product.msdSheets){
					this.fileList = this.product.msdSheets.data;
					this.msdFiles = this.product.msdSheets.data;
				}
				this.properShippingDisabled = false;
				this.sizes = this.product.sizes.data;
			}
			// Ensures that the initial values are bound correctly
			this.autocompleteReady = true;
		},

        methods: {			
			editSize(size) {
				this.sizeEditing = size;
				this.editingForm = {
					code: size.code,
					amount: size.amount,
					unit: size.unit
				}
				// this.sizeEditing = null;
			},
			
			unSelected (value) {
				this.unError = false;
				this.sizesError = false;
				this.form.unId = value.selectedObject.value;
				this.form.unName = value.display;
				this.getShippingName(this.form.unId);
			},

            submitForm() {
				this.$validator.validateAll().then(res => {
					if (res) {
						if (!this.form.unId) {
							this.unError = true;
						}else if(!this.tempSizes.length && !this.sizes.length){
							this.sizesError = true;
						}else {
							this.saving = true;
							if (this.product) {
								this.updateDangerousGoods();
							} else {
								this.createDangerousGoods();
							}
						}
					}
				});
            },

            createDangerousGoods() {
                axios.post('/send-freight/dangerous-goods', {
                        'name': this.form.commonName,
						'un': this.form.unId,
						'flash_point': this.form.flashPoint,
						'marine_pollutant': this.form.marinePollutant,
						'proper_shipping_name' : this.form.properShippingName,
                    })
                    .then(response => {
						this.form = {
							commonName: this.productName,
							unId: this.productUnId,
							unName: this.productUnName,
						}
						this.handleDeliveryUpload(response.data.data.id);
						this.saving = false;
						this.addSize(response.data);
						this.$emit('submitted', response.data.data);
                    })
					.catch(response => {
						this.saving = false;
						Event.$emit('serverError');
						this.$emit('cancelled');
					});
            },

            updateDangerousGoods() {
                axios.patch('/send-freight/dangerous-goods/' + this.product.id, {
                        'name': this.form.commonName,
						'un': this.form.unId,
						'flash_point': this.form.flashPoint,
						'marine_pollutant': this.form.marinePollutant,
						'proper_shipping_name' : this.form.properShippingName,
                    })
                    .then(response => {
						this.addSize(response);
						this.$emit('submitted');
						this.handleDeliveryUpload(response.data.id);
						this.saving = false;
						this.$swal({
							title: "Dangerous good updated!",
							type: "success",
							confirmButtonText: 'Okay',
						});
                    })
					.catch(response => {
						this.saving = false;
						Event.$emit('serverError');
						this.$emit('cancelled');
					});
			},

			tempAddsize(){
					this.sizeSaving = true;
						axios.post('/send-freight/dangerous-goods/' + this.product.id + '/sizes', {
							code: this.sizesForm.code,
							amount: this.sizesForm.amount,
							unit:  this.sizesForm.unit,
						})
						.then(response => {
							this.sizes.push({
								id: response.data.data.id,
								amount: this.sizesForm.amount,
								unit: this.sizesForm.unit,
								code: this.sizesForm.code
							})
							this.sizeSaving = false;
						});
			},

			tempsize(){
				this.tempSizes.push({
					id: this.idtest++,
					code: this.sizesForm.code,
					amount: this.sizesForm.amount,
					unit: this.sizesForm.unit
				})
				this.sizesForm = {
						amount: '',
						unit: 'kg',
						code: ''
				}
				this.sizesError = false;
			},

			deleteTempSize(index){
				this.tempSizes.splice(index, 1)
			},
			
			addSize(response) {		
				for(let i = 0; i < this.tempSizes.length; i++){
						axios.post('/send-freight/dangerous-goods/' + response.data.id + '/sizes', {
							code: this.tempSizes[i].code,
							amount: this.tempSizes[i].amount,
							unit: this.tempSizes[i].unit
						})
						.then(response => {
							this.sizes.push({
								id: response.data.data.id,
								amount: this.sizesForm.amount,
								unit: this.sizesForm.unit,
								code: this.sizesForm.code
							})
						});
					

					}
						
			},

			updateSize(size, sizeIndex){
				this.sizeUpdating = size;
				axios.patch('/send-freight/dangerous-goods/' + this.product.id + '/sizes/' + size.id, {
					code: this.editingForm.code,
					unit: this.editingForm.unit,
					amount: this.editingForm.amount,
				})
				.then(response => {
					this.sizeUpdating = null;
					this.sizeEditing = null;
					this.sizes.push({
						id: size.id,
						amount:this.editingForm.amount,
						unit: this.editingForm.unit,
						code: this.editingForm.code,
					})
					this.sizes.splice(sizeIndex, 1); 
					
				})
				.catch(response => {
						Event.$emit('serverError');
						this.$emit('cancelled');
				});
			},

			deleteSize(sizeIndex) {
			
				this.sizeDeleting = sizeIndex;

				axios.delete('/send-freight/dangerous-goods/' + this.product.id + '/sizes/' + this.sizes[sizeIndex].id)
					 .then(response => {
						this.sizes.splice(sizeIndex, 1); 
						this.sizeDeleting = null;
					 });
			},

			hasProduct(){
				if(this.product !== null){
					return true;
				}
				return false;
			},
			getShippingName(unId){
				axios.get('/api/un-numbers/' + unId)
				.then(res => {
					this.form.properShippingName = res.data;
					this.properShippingDisabled = false;
				});
			},
			duringDeliveryUpload(file){
				this.msdFiles.push(file);
			},
			handleDeliveryUpload(dgId){
				for(let i = 0; i < this.msdFiles.length; i++){
					let formData = new FormData();
					formData.append("file", this.msdFiles[i]);
					formData.append('_method', 'POST');
					axios.post('/send-freight/dangerous-goods/'+dgId+'/uploadMSD/',
						formData,
						{
							headers: {
								'Content-Type': 'multipart/form-data'
							}
						}).then(res => {
							this.errors.remove('msdFile');
						}).catch(e => {
							console.log(e);
						})
				}
			},
			handleRemove(file){
				this.msdFiles = this.msdFiles.filter(function( obj ) {
							return obj.id !== file.id;
						});
				//Dont try to delete only locally uploaded file
				if(file.id){
					axios.delete('/send-freight/dangerous-goods/' + file.id + '/msdSheet/')
					.then(res => {
						this.errors.remove('msdFile');
					});
				}
			}
        },
		computed: {
			submitButtonText() {
				return "Save";
			},

			modalTitle() {
				if (this.product) {
					return "Edit: "+this.product.name;
				}

				return "Create new dangerous good";
			},

			productName(){
				return this.form.commonName;
			},

			productUnId(){
				return this.form.unId;
			},

			productUnName(){
				return this.form.unName;
			},

			productID(){
				return this.response.data.data;
			},
		},
    }
</script>


<style scoped>
    >>> .el-upload-dragger { 
				width: 400px; 
				height: 100px;
				}
	>>> .el-upload__tip { 
				width: 400px; 
				}
	>>> .el-icon-upload { 
				margin-top: 7px; 
				}
	>>> .el-upload-list__item-name { 
				white-space: normal !important;  
                }
</style>